import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { HelpCircle, X } from 'lucide-react';

const HELP_CONTENT = {
  direccion: {
    title: 'La vista de direccion',
    content: `Esta pantalla es para decidir, no para trabajar: no tiene ninguna acción.

Contesta cinco preguntas, cada una con su tendencia:

1. ¿La demanda supera a la capacidad? Entradas contra salidas, y el backlog que queda. Un área puede cerrar más casos que nunca y estar hundiéndose; eso sólo se ve con las dos series juntas.

2. ¿Cumplimos lo prometido? Los dos relojes. El de atención —cuánto tarda alguien en dar señales de vida— suele importar más que el de resolución.

3. ¿Se trabaja o se interrumpe? Traspasos, repriorizaciones, reaperturas y altas manuales. Nada de eso es trabajo nuevo, y todo consume el día.

4. ¿En qué se va el trabajo? Las categorías con más volumen, coloreadas por cumplimiento. Y aparte, cuánto quedó sin clasificar: si eso sube, el clasificador se rompió y no avisa de otra forma.

5. ¿Sirvió? La conformidad de quien pidió, siempre con cuántos contestaron al lado.

Los números salen de las unidades de trabajo con compromiso: lo que no tiene reloj queda fuera del denominador, así que un cumplimiento alto no puede venir de casos que nadie prometió.`,
  },
  capsule: {
    title: 'Configuracion de Capsula',
    content: `**Manual de configuracion:**
La politica de la capsula se define con guias Markdown (routing, tickets, SLA), listas opcionales (labels, intents, prioridades), skill_policy y parametros de coordinacion (kb_retrieval_gate, max_coordinator_tool_rounds).

**No hay matrices routing_rules:** el coordinador clasifica con SOUL/RULES + guias inyectadas en prompts LLM.

**Donde ver el manual completo:** pantalla Manual (menu lateral), o el boton Ayuda de Config Capsula para la ayuda de esa pantalla.`,
  },
  agents: {
    title: 'Configuracion de Agentes',
    content: `**Como funcionan los agentes:**
Cada agente es una "personalidad" con comportamiento propio, definido por sus archivos .md y configuraciones.

**Configs (.md):**
- **SOUL.md**: Define la identidad, personalidad, idioma y objetivo del agente
- **RULES.md**: Define las reglas de comportamiento, que puede y no puede hacer
- **MEMORY.md**: Aprendizajes acumulados, patrones conocidos, soluciones frecuentes
- Usa el boton **AI** (icono de chispa) para generar contenido automaticamente desde una breve descripcion

**Modelo LLM:** Configura que modelo de IA usa cada agente (proveedor, modelo, temperatura, tokens). El "fallback" se usa si el primario falla.

**Habilidades (documentos Markdown):**
Las habilidades son documentos Markdown ejecutables que dan capacidades reales a los agentes:
- Cada habilidad define CUANDO activarse, instrucciones, y herramientas HTTP
- Las habilidades se definen una vez y se comparten entre multiples agentes
- Cada agente puede tener un **override** (instrucciones adicionales) para personalizar la habilidad sin duplicarla
- Las herramientas definidas en una habilidad (ej: buscar codigo en GitLab, crear issues) se ejecutan en tiempo real via HTTP
- Para agregar una nueva integracion, solo necesitas escribir un documento .md con las herramientas
- Usa el boton **AI** para generar habilidades y overrides automaticamente

**Canales:** Vincula el agente a canales de comunicacion (WhatsApp, Teams, etc). Un agente puede operar en multiples canales.

**Test Coordinador:** Prueba la clasificacion del coordinador escribiendo un mensaje de ejemplo.

**Consejo:** Agregar una nueva integracion = escribir un archivo .md. No requiere tocar codigo Python.`,
  },
  knowledge: {
    title: 'Base de Conocimiento',
    content: `**Como funciona la KB:**
Origenes (mounts, repos Git, archivos de capsula, artefactos de ticket) se fragmentan e indexan en Qdrant. El agente recupera con scope de capsula/agente (Playground para probar).

**Pestanas:** Playground, Grafo, Contenido, Origenes, Configuracion, Memoria (libro de historia), Ayuda.

**Memoria organizacional (importante):**
- Crear un ticket (mail a Del.ia, WhatsApp, chat) **NO** genera un suceso. No es demora: el disparador es resolver/cerrar el ticket (extraccion automatica) o "Extraer borrador" en Tickets → Memoria.
- Luego revisar → Promover (gate humano) → queda approved e indexado. Capítulos/indices los arma el curador.
- Parametrico = snapshots ERP estables; Playbooks = procedimientos SME; distinto del codigo en mounts. Nada enruta solo un ticket a esos canales.
- Curacion: Archivar/Eliminar sucesos y Eliminar snapshots/playbooks (incl. demos) en Base de Conocimiento → Memoria. Descartar borrador en Tickets → Memoria.
- Guia completa: Base de Conocimiento → Ayuda (seccion Memoria).

**Consejo:** Para que un agente use la KB clasica necesita habilidad de busqueda; la historia aprobada se inyecta en runtime si el flag RUNTIME_INJECT esta activo.`,
  },
  channels: {
    title: 'Canales de Comunicacion',
    content: `**Como funcionan los canales:**
Los canales son las vias de comunicacion por las que los usuarios contactan al soporte.

**WhatsApp (Baileys):**
1. Crea un canal tipo WhatsApp
2. Click "Conectar WhatsApp" para generar el codigo QR
3. Escanea el QR con tu telefono (WhatsApp > Dispositivos vinculados)
4. El canal queda conectado y recibe mensajes
5. En la seccion de Agentes, vincula los agentes que deben responder por este numero

**Nota:** Baileys usa el protocolo de WhatsApp Web (scraping). El telefono debe mantener conexion a internet. El microservicio whatsapp-bridge corre automaticamente en el puerto 3100.

**Varios agentes, un numero:** Multiples agentes pueden estar vinculados al mismo numero. El Coordinador decide quien responde segun la clasificacion del mensaje.

**Proximo:** Microsoft Teams y Email via Microsoft Graph API.

**Consejo:** Un canal se crea UNA vez y se vincula a los agentes que correspondan en la seccion de Agentes > tab Canales.`,
  },
  channels_remitentes_admitidos: {
    title: 'Quién atiende este canal',
    content: `**Para qué sirve:** cada canal declara a quién atiende. Quien no coincide con ninguna regla se descarta **en silencio**: no se le responde (responder confirma que la casilla existe), no se crea su contacto y no llega a ningún agente. Queda una línea en el log de diagnóstico con quién escribió y por qué no entró, sin el texto del mensaje.

**Sin reglas, el canal atiende a cualquiera**, como antes.

**Correo y Teams**
- **Dominios**: \`pritty.com.ar\`. Coinciden exactos: un subdominio se carga aparte.
- **Correos puntuales**: para admitir a alguien de un dominio que no está en la lista.
- En Teams se usa el correo que da el directorio. Si no se puede saber quién escribe y el canal tiene reglas, no entra.

**WhatsApp**
- **Prefijos**: \`+54\` (todo el país) o \`+54351\` (la característica de Córdoba). Alcanzan a los celulares con y sin el 9.
- **Números puntuales**: se pueden escribir con o sin el 9; se guardan en formato internacional.
- En un grupo permitido, la regla se aplica a quien escribe.

**Excluidos**
- **Correos excluidos** y **números excluidos**: cuentas o números puntuales que el canal nunca atiende, aunque su dominio o su prefijo estén admitidos. Por ejemplo: todo \`@pritty.com.ar\` menos \`comunicacionessistemas@pritty.com.ar\`, una casilla que manda avisos y no pide nada.
- **Ganan siempre** sobre las reglas de admisión. En un canal sin reglas de admisión, el canal atiende a todos menos a los excluidos.

**Lo que no cambia:** lo que el sistema manda (avisos, respuestas a quien ya es contacto) sale igual. Los contactos que ya existen quedan como están.`,
  },
  channels_cuenta_conectada: {
    title: 'Cuenta conectada (Teams)',
    content: `**Para qué sirve:** que Teams lea y conteste **como la cuenta del canal**, sin guardar su contraseña.

**Cómo se conecta**
- Tocá «Conectar cuenta de Microsoft». Se abre el inicio de sesión de Microsoft: entrá con la cuenta del canal (alguien que la tenga).
- OmniSyS comprueba que la cuenta que entró sea la del canal. Si es otra, no guarda nada.
- Se guarda solo la autorización, cifrada. Ninguna contraseña.

**Quién decide qué cuentas se pueden leer:** un administrador de Microsoft 365, que habilita cada cuenta para la app. Si Microsoft dice que hace falta la aprobación de un administrador, esa cuenta todavía no está habilitada.

**Si dice «hay que volver a conectarla»:** Microsoft dejó de aceptar la autorización (cambió la contraseña, se revocaron las sesiones o se quitó el permiso). Mientras tanto Teams no lee ni contesta con esa cuenta, y los administradores reciben un aviso. Se arregla conectándola de nuevo.

**Por qué Teams entra solo por barrido:** el aviso de Microsoft para chats exige un permiso de aplicación que deja leer los chats de cualquier cuenta, y se retiró a propósito.`,
  },
  channels_atencion: {
    title: 'Atención del canal',
    content: `**Para qué sirve:** decidir si el canal recibe mensajes y ver qué agentes lo atienden.

**Activo o pausado**
- **Pausado**, el canal no recibe nada: no se barre, la sincronización a mano no lo lee y los mensajes de WhatsApp se ignoran. No hace falta borrarlo para dejar de atenderlo.
- Lo que **sale** no se corta: un canal pausado puede seguir mandando respuestas y avisos (por ejemplo, una casilla que solo se usa para avisar).
- Al volver a activarlo, empieza desde ese momento: lo que llegó mientras estaba pausado no se contesta.

**Qué agentes lo atienden**
- Un canal lo atienden **solo los agentes que lo tienen marcado** en Agentes → (el agente) → Canales.
- **Si ningún agente lo tiene marcado, no se atiende**: el mensaje se descarta sin responder, no se crea el contacto y queda en el log de diagnóstico (\`canal.canal_sin_agente\`).
- En una cápsula con varios agentes, marcá el canal en cada uno que pueda atenderlo. Si el coordinador elige un agente que no lo tiene, contesta el primero que sí.`,
  },
  channels_ingreso_m365: {
    title: 'Cómo entra lo nuevo (Microsoft 365)',
    content: `**Para qué sirve:** elegir cómo se entera OmniSyS de un correo o un mensaje de Teams nuevo en la casilla del canal.

**Barrido automático**
- OmniSyS revisa la casilla cada N minutos (de 1 a 60). Cada barrido mira desde el anterior, con un margen, y nunca más de 6 horas para atrás.
- Funciona en cualquier red: solo hace falta que el servidor salga a Microsoft. Es el modo de los canales nuevos.
- Un canal recién creado, o recién prendido, **empieza desde ese momento**: lo que ya estaba en la casilla no se contesta de golpe.
- Si un mensaje falla antes de llegar al agente, el próximo barrido lo vuelve a intentar.

**Aviso de Microsoft (webhook)**
- Microsoft avisa al instante. Necesita que Microsoft pueda llegar a la URL pública del servidor (\`MS_GRAPH_WEBHOOK_BASE_URL\`): un firewall, Cloudflare o un filtro por país lo pueden frenar.
- Al activarlo se crea la suscripción en ese momento. **Si Microsoft la rechaza, el canal sigue como estaba** y se muestra el motivo.
- Igual corre un barrido de respaldo cada 15 minutos, por si un aviso no llega. En Teams, Microsoft no entrega los avisos del chat sin un registro aparte, así que en la práctica entra por el respaldo.

**Traer mensajes anteriores:** atiende lo que llegó en las últimas horas y todavía no se atendió. Sirve, por ejemplo, después de crear un canal.

**Nunca se contesta dos veces lo mismo.** El barrido, el aviso y el botón pasan por la misma puerta: cada mensaje se reserva en su casilla antes de llegar al agente. Si dos caminos lo traen a la vez, lo atiende uno solo. Y como la marca es de la casilla y no del canal, borrar un canal y crearlo de nuevo no vuelve a contestar lo ya contestado.`,
  },
  channels_whatsapp_inbound: {
    title: 'Politica de turnos inbound (WhatsApp)',
    content: `**group_policy vs inbound_turn.group_trigger**
Son capas distintas: **group_policy** (deny_all / allowlist) decide si el grupo esta permitido a nivel canal antes de procesar mensajes. Si el grupo no pasa, no hay conversacion ni agente. **inbound_turn.group_trigger** aplica ya dentro de un grupo permitido: controla si cada mensaje dispara al bot (every_message), solo si hay mencion al numero del canal (mention_only), o nunca en grupo (never). No contradigas: si group_trigger es mention_only, las RULES del agente no deben asumir que siempre vera todos los mensajes de grupo.

**Modos (mode)**
- **immediate**: cada mensaje usuario dispara el turno del agente en cuanto entra (tras persistencia).
- **debounce_idle**: espera idle_quiet_ms de silencio sin nuevos mensajes usuario y luego fusiona el tramo y ejecuta un solo turno.
- **debounce_idle_or_typing**: igual que debounce_idle pero si el bridge indica que el usuario sigue escribiendo (typing), se aplaza hasta que deje de escribir o venza max_batch_window_ms.

**Parametros clave**
- **idle_quiet_ms**: milisegundos de pausa sin mensajes nuevos antes de cerrar el lote y responder.
- **max_batch_window_ms**: tope desde el primer mensaje del lote; pasado ese tiempo se fuerza respuesta aunque siga la senal de escritura (evita esperas infinitas).
- **merge_user_text**: concat_newlines une varios mensajes usuario con saltos; last_only usa solo el ultimo contenido para el prompt.

**debounce_backend: memory vs mongo**
- **memory**: temporizador en el proceso del backend (una sola instancia). Simple y suficiente si solo hay un worker.
- **mongo**: encola el flush en la coleccion wa_inbound_debounce_jobs; un worker en cada replica puede reclamar trabajos. Usar cuando hay **varias replicas** del API sin sticky sessions y se necesita un unico flush coherente por conversacion.

**Alineacion con RULES (agente)**
La politica estructurada (inbound_turn + group_policy) manda el **gating**: cuando entra texto al agente, puede venir ya fusionado por debounce. Las RULES deben describir **tono, herramientas y como responder al texto ya fusionado**, sin asumir un mensaje por linea si merge_user_text agrupa. No contradecir group_trigger (ej. si solo respondes con mencion, no pidas al usuario que escriba en grupo sin @bot).

**Ejemplos de frases utiles en RULES**
- "Si el mensaje usuario incluye varios parrafos fusionados, respondé en un solo bloque cubriendo todos los puntos."
- "En grupos solo actuas si el sistema ya te enrolo por mencion; no insistas en que el usuario repita sin @."
- "Mantene tono breve en WhatsApp; prioriza checklist y siguiente paso concreto."`,
  },
  monitor: {
    title: 'Monitor HITL',
    content: `**Como funciona el monitor:**
HITL = Human-in-the-Loop. Cuando una conversacion se escala al nivel HITL, aparece aqui para que un supervisor tome el control.

**Takeover:** Toma el control de la conversacion. El agente deja de responder automaticamente y vos contestas directo al usuario.

**Handback:** Devuelve el control al agente automatico.

**WebSocket en vivo:** Las actualizaciones llegan en tiempo real sin necesidad de refrescar.

**Consejo:** Una conversacion queda marcada como que necesita a una persona cuando el agente lo pide o cuando el usuario deja de responder.`,
  },
  chat: {
    title: 'WebChat',
    content: `**Como funciona el chat:**
Este es el canal de WebChat integrado. Cuando envias un mensaje:

1. El Coordinador clasifica tu mensaje (intent, prioridad, categoria)
2. Segun las reglas de routing de la capsula, se asigna un agente
3. El agente carga sus habilidades y busca en la base de conocimiento (si tiene esa habilidad habilitada)
4. Si el agente tiene habilidades con herramientas (ej: GitLab), puede ejecutar acciones reales (buscar codigo, crear issues)
5. El agente responde usando su SOUL.md + RULES.md como guia
6. Si se cumplen las reglas de tickets, se crea un ticket automaticamente
7. Si el agente no puede resolver, se escala al siguiente nivel

**Tool Calling:** Si un agente tiene habilidades con herramientas HTTP definidas, el LLM puede decidir usarlas automaticamente. Por ejemplo, el agente de Soporte N2 puede buscar codigo en GitLab si tiene la habilidad habilitada.

**Consejo:** El comportamiento del chat depende 100% de la configuracion de la capsula, los agentes, y las habilidades asignadas. No hay logica hardcodeada.`,
  },
  tickets: {
    title: 'Tickets',
    content: `**Como funcionan los tickets:**
Los tickets se crean automatica o manualmente segun las reglas configuradas en la capsula.

**Creacion automatica:** Cuando la clasificacion del Coordinador cumple las condiciones definidas (intent + prioridad), se crea un ticket con el label correspondiente.

**Labels:** Se asignan segun el mapeo "intent → label" configurado en la capsula.

**Cuando hace falta una persona:** el agente crea una tarea humana con dueño, y a esa persona le llega un aviso.

**Integracion con GitLab:** Si un agente tiene la habilidad de GitLab habilitada, puede crear issues en GitLab vinculados al ticket.

**Consejo:** Si no se estan creando tickets cuando deberian, revisa las reglas de tickets en la configuracion de la capsula.`,
  },
  dashboard: {
    title: 'Dashboard',
    content: `**Dashboard principal:**
Vista general del estado del sistema de soporte. Muestra metricas rapidas de tickets, conversaciones activas y agentes.

**Consejo:** Las metricas se actualizan en tiempo real.`,
  },
  team: {
    title: 'Equipo y Seguimiento (Heartbeat)',
    content: `**Directorio de Equipo:**
Los miembros del equipo son las personas de tu organizacion que participan en el soporte. Cada miembro tiene:
- **Expertise**: Areas de conocimiento (ej: Oracle Forms, PL/SQL, Redes)
- **Canales**: Vias de contacto ordenadas por prioridad (WebChat, WhatsApp, Teams, Email)
- **Estado**: Activo, Vacaciones, No disponible
- **Notificaciones**: Si recibe alertas cuando un agente necesita su intervencion

**Heartbeat (Seguimiento de Respuestas):**
Motor que monitorea conversaciones activas y toma accion cuando un usuario no responde:
- **Timeout**: Tiempo de espera antes del primer recordatorio (ej: 30 minutos)
- **Max Recordatorios**: Cuantos recordatorios enviar antes de escalar
- **Accion al agotar**: Que hacer si el usuario no responde despues de los recordatorios (crear ticket, notificar equipo, o ambos)
- Si el usuario dice un plazo ("te respondo manana"), el sistema respeta ese tiempo
- Los recordatorios salen por el mismo canal de la conversacion (WebChat, WhatsApp, etc.)

**Notificaciones Agente-Humano:**
Los agentes pueden solicitar activamente que un humano del equipo intervenga:
- El sistema busca al miembro mas adecuado segun expertise
- Le envia un mensaje por su canal de mayor prioridad
- Si no responde, intenta con el siguiente canal o el siguiente miembro

**Flujo completo:**
1. Agente hace pregunta al usuario
2. Si el usuario no responde en X minutos → recordatorio automatico
3. Si despues de N recordatorios sigue sin respuesta → crear ticket + notificar al equipo
4. El miembro notificado puede tomar la conversacion desde el Monitor HITL

**Consejo:** Configura el heartbeat desde la pestana "Heartbeat". Agrega miembros del equipo con sus canales y expertise para que las notificaciones lleguen a la persona correcta.`,
  },
  logs: {
    title: 'Logs del sistema',
    content: `**Dos pestanas en esta pantalla:**
1. **Errores e incidencias (BD)**: registros persistidos en MongoDB (tenant, capsula, origen backend/frontend/mongodb/qdrant, accion, detalle). Sirve para diagnosticar fallas de API, validacion, agente, red cliente, etc.
2. **Autopsia conversaciones**: traza paso a paso por conversacion (eventos de orquestacion/RAG/agente).

**Abrir cualquier conversation_id:**
La lista lateral solo enumera hilos omnicanal. El Asistente IA del ticket usa IDs \`ticket-chat-{ticket_id}\`. Usa el campo **Abrir conversation_id** o el deep link desde Tickets → chat (\`/logs?tab=autopsy&conversation=ticket-chat-...\`). Tambien existen eventos \`turn_health\` (veredicto del harness) cuando autopsia esta Activa.

**Como funciona la autopsia:**
El sistema registra CADA paso de lo que ocurre desde que un usuario envia un mensaje hasta que recibe respuesta. Es una radiografia completa del procesamiento.

**Fases registradas:**
- **INPUT**: Mensaje recibido (canal, remitente, texto)
- **ORCHESTRATION**: Clasificacion del coordinador (intent, prioridad, agente sugerido, confianza, razon), decision de routing (que regla aplico), label asignado (por que ese label)
- **KNOWLEDGE**: Busqueda RAG (query, resultados, fuentes encontradas, scores)
- **AGENT**: Procesamiento del agente (modelo LLM usado, prompt enviado, configs cargadas, habilidades activas, contexto), respuesta generada
- **TOOL_EXECUTION**: Herramientas ejecutadas por el agente (nombre, parametros, resultado, exito/error)
- **TICKETING**: Creacion de ticket (auto o manual, regla que disparo)
- **OUTPUT**: Respuesta enviada al usuario (canal, agente)
- **HITL**: Acciones humanas (takeover, handback)

**Toggle On/Off:** Usa el switch para activar/desactivar el logging. Cuando esta desactivado, no se registran eventos nuevos (los existentes se mantienen).

**Expandir eventos:** Click en cualquier evento para ver TODOS los detalles: prompt exacto enviado al LLM, modelo y temperatura usados, fuentes RAG inyectadas, regla de routing que aplico, herramientas ejecutadas con parametros y resultados, etc.

**Consejo:** Si algo no funciona como esperas, busca la conversacion aqui y expande cada paso para entender exactamente que decidio el sistema y por que.`,
  },
};

export default function HelpButton({ section }) {
  const [open, setOpen] = useState(false);
  const help = HELP_CONTENT[section];

  if (!help) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`help-button-${section}`}
        className="rounded-sm text-xs h-8 text-brand-primary border-brand-primary/30 hover:bg-blue-50"
      >
        <HelpCircle className="w-3.5 h-3.5 mr-1" /> Ayuda
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-sm border border-brand-border shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto"
            onClick={e => e.stopPropagation()} data-testid={`help-modal-${section}`}>
            <div className="flex items-center justify-between p-4 border-b border-brand-border bg-zinc-50">
              <h2 className="text-sm font-bold uppercase tracking-[0.1em] text-[#09090B] flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-brand-primary" /> {help.title}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-7 w-7 p-0"><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4 text-sm text-zinc-700 leading-relaxed whitespace-pre-line">
              {help.content.split('\n').map((line, i) => {
                if (line.startsWith('**') && line.endsWith('**')) {
                  return <h3 key={i} className="font-bold text-[#09090B] mt-3 mb-1 text-sm">{line.replace(/\*\*/g, '')}</h3>;
                }
                if (line.startsWith('**')) {
                  const [bold, ...rest] = line.split('**').filter(Boolean);
                  return <p key={i} className="mb-1"><strong>{bold}</strong>{rest.join('')}</p>;
                }
                if (line.startsWith('- ')) return <p key={i} className="ml-3 mb-0.5 text-xs">{line}</p>;
                if (line.trim() === '') return <br key={i} />;
                return <p key={i} className="mb-1 text-xs">{line}</p>;
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
